import { CAMERA, TERRAIN } from "./constants";
import type { TerrainPoint } from "./types";

export function generateInitialTerrain(canvasHeight: number): TerrainPoint[] {
  const controlPoints: TerrainPoint[] = [];

  controlPoints.push({ x: -canvasHeight / 4, y: canvasHeight * 0.6 });
  controlPoints.push({ x: 0, y: canvasHeight * 0.4 });

  const mainHills = generateControlPoints(
    0,
    canvasHeight * 0.4,
    canvasHeight,
    TERRAIN.NUM_HILLS,
  );
  controlPoints.push(...mainHills);

  return generateSmoothTerrain(controlPoints);
}

export function generateControlPoints(
  startX: number,
  startY: number,
  canvasHeight: number,
  numHills: number,
  initialSign = 1,
): TerrainPoint[] {
  const points: TerrainPoint[] = [];
  const baselineY = canvasHeight * CAMERA.BASELINE_FACTOR;

  let x = startX;
  let y = startY;
  let sign = initialSign;

  for (let i = 0; i < numHills; i++) {
    const dx = 160 + Math.random() * 80;
    x += dx;

    const dy =
      TERRAIN.MIN_HILL_HEIGHT +
      Math.random() * (TERRAIN.MAX_HILL_HEIGHT - TERRAIN.MIN_HILL_HEIGHT);
    let ny = y + dy * sign;

    const maxTerrainHeight = baselineY - 100;
    const minTerrainHeight = canvasHeight - TERRAIN.PADDING_TOP;

    ny = Math.max(maxTerrainHeight, Math.min(minTerrainHeight, ny));

    ny = Math.max(
      TERRAIN.PADDING_BOTTOM,
      Math.min(canvasHeight - TERRAIN.PADDING_TOP, ny),
    );

    const distanceFromBaseline = Math.abs(ny - baselineY);
    if (distanceFromBaseline > 150) {
      if (ny < baselineY) {
        sign = 1;
      } else {
        sign = -1;
      }
    } else {
      sign *= -1;
    }

    y = ny;
    points.push({ x, y });
  }

  return points;
}

export function generateSmoothTerrain(
  controlPoints: TerrainPoint[],
): TerrainPoint[] {
  const terrain: TerrainPoint[] = [];

  for (let i = 0; i < controlPoints.length - 1; i++) {
    const p0 = controlPoints[i];
    const p1 = controlPoints[i + 1];
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const segments = Math.floor((p1.x - p0.x) / TERRAIN.SEGMENTS_PER_HILL);

    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const interpolated = interpolatePoints(p0, p1, Math.max(segments, 1));

    if (i < controlPoints.length - 2) {
      terrain.push(...interpolated.slice(0, -1));
    } else {
      terrain.push(...interpolated);
    }
  }

  return terrain;
}

export function interpolatePoints(
  p0: TerrainPoint,
  p1: TerrainPoint,
  segments: number,
): TerrainPoint[] {
  const points: TerrainPoint[] = [];
  const ymid = (p0.y + p1.y) / 2;
  const amplitude = (p0.y - p1.y) / 2;

  for (let j = 0; j <= segments; j++) {
    const progress = j / segments;
    const x = p0.x + progress * (p1.x - p0.x);
    const angle = Math.PI * progress;
    const y = ymid + amplitude * Math.cos(angle);
    points.push({ x, y });
  }

  return points;
}

export function extendTerrain(
  terrain: TerrainPoint[],
  canvasHeight: number,
): void {
  if (terrain.length < 2) return;

  const lastPoint = terrain[terrain.length - 1];
  const secondLastPoint = terrain[terrain.length - 2];

  const lastSegmentSlope =
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    (lastPoint.y - secondLastPoint.y) / (lastPoint.x - secondLastPoint.x);
  const lastSign = lastSegmentSlope > 0 ? -1 : 1;

  const newControlPoints = generateControlPoints(
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    lastPoint.x,
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    lastPoint.y,
    canvasHeight,
    TERRAIN.NUM_HILLS,
    lastSign,
  );

  const allControlPoints = [lastPoint, ...newControlPoints];

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const newTerrain = generateSmoothTerrain(allControlPoints);

  terrain.push(...newTerrain.slice(1));
}

export function cleanupOldTerrain(
  terrain: TerrainPoint[],
  cameraX: number,
  frameCount: number,
): void {
  if (frameCount % TERRAIN.CLEANUP_INTERVAL !== 0) return;

  const minTerrainLeft = cameraX - TERRAIN.CLEANUP_DISTANCE;
  let keepFromIndex = 0;

  for (const [i, element] of terrain.entries()) {
    if (element.x > minTerrainLeft) {
      keepFromIndex = Math.max(0, i - 10);
      break;
    }
  }

  if (keepFromIndex > TERRAIN.CLEANUP_THRESHOLD) {
    terrain.splice(0, keepFromIndex);
  }
}

export function getTerrainY(x: number, terrain: TerrainPoint[]): number {
  if (terrain.length < 2) {
    return terrain[0]?.y ?? 0;
  }

  let i = 0;
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  while (i < terrain.length - 1 && terrain[i + 1].x < x) {
    i++;
  }

  if (i >= terrain.length - 1) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return terrain[terrain.length - 1].y;
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (i === 0 && x < terrain[0].x) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return terrain[0].y;
  }

  const p0 = terrain[i];
  const p1 = terrain[i + 1];

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (Math.abs(p1.x - p0.x) < 0.001) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return p0.y;
  }

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const t = Math.max(0, Math.min(1, (x - p0.x) / (p1.x - p0.x)));
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return p0.y + t * (p1.y - p0.y);
}

export function getTerrainSlope(x: number, terrain: TerrainPoint[]): number {
  const delta = 8;
  const y1 = getTerrainY(x - delta, terrain);
  const y2 = getTerrainY(x + delta, terrain);
  const slope = (y2 - y1) / (2 * delta);
  return Math.max(-2, Math.min(2, slope));
}
